import React, { useRef, useState } from 'react';
import { Modal, Form, Select, Button, RefSelectProps, InputRef, Spin } from 'antd';
import { SalesforceAccountSearch } from 'components/atoms/SalesforceAccountSearch';
import { WechatOutlined } from '@ant-design/icons';
import { useExtraApi } from 'sb/api/extra';
import { Contract } from 'sb/models/Contract';
import { useChatState } from '../state/chatState';
import moment from 'moment';
import { CategorizedProjects, Project, GroupedSessionsProject } from 'api/useChat';

interface NewProjectModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

const NewDealProjectModal: React.FC<NewProjectModalProps> = ({ open, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const salesForceClientRef = useRef<RefSelectProps>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [salesForceDealsLoading, setSalesForceDealsLoading] = useState(false);

  // hooks
  const { getAccountContracts } = useExtraApi();
  const { state, createNewDealProject, dispatch } = useChatState();

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async (values: any) => {
    createNewDealProject(values?.filterByVendor);
    form.resetFields();
    onSubmit();
  };

  const getSalesForceDeals = async (account_id: string, salesforce_deal_id?: any) => {
    if (!account_id) return;

    setSalesForceDealsLoading(true);
    const data = await getAccountContracts(account_id);
    if (data) {
      setContracts(data);
    }
    setSalesForceDealsLoading(false);
  };

  const handleClientSelectChange = async (item: any | undefined) => {
    if (!item?.value) return;

    await getSalesForceDeals(item.value);
  };

  return (
    <Modal open={open} title="New Deal Project" onCancel={handleCancel} footer={null}>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="searchSalesforce" rules={[{ required: true, message: 'Please select Client' }]}>
          <SalesforceAccountSearch
            ref={salesForceClientRef}
            width="100%"
            onChange={handleClientSelectChange}
            placeholder="Search for Clients"
          />
        </Form.Item>
        <Spin spinning={salesForceDealsLoading}>
          <Form.Item name="filterByVendor" rules={[{ required: true, message: 'Please select Deal' }]}>
            <Select
              className="w-full"
              size="large"
              placeholder="Filter by Deal or Supplier"
              optionFilterProp="children"
              showSearch
              showArrow
              allowClear
            >
              {contracts
                .sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''))
                .map((item: Contract) => (
                  <Select.Option value={item.contract_id} key={item.contract_id}>
                    {item?.vendor_name} - {item?.salesforce_deal_name} - {item?.subject} -{' '}
                    {moment(item?.creation_date).utc().format('ll')}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Spin>
        <div className="flex justify-start">
          <Button size="large" key="submit" type="primary" htmlType="submit" icon={<WechatOutlined />}>
            Start Project
          </Button>
          <Button size="large" key="cancel" type="text" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default NewDealProjectModal;
